import * as React from "react";
import { useTranslation } from "react-i18next";
import Editor from "ckeditor5-custom-build";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import { makeStyles } from "@material-ui/core";
import { isEmptyText } from "src/helpers";

const editorConfig = {
  toolbar: ["bold", "italic", "bulletedList", "numberedList", "link"]
};

const useStyles = makeStyles((theme: any) => ({
  CKEditorWrapper: {
    "& .ck-content": {
      "& li": {
        margin: "0 0 0 1.2em !important"
      },
      "& ol": {
        margin: "0 !important",
        padding: "0"
      },
      "& p": {
        margin: "0 !important"
      },
      "& ul": {
        margin: "0 !important",
        padding: "0"
      },
      [theme.breakpoints.up("md")]: {
        fontSize: "1.4rem"
      },
      border: "none !important",
      boxShadow: "none !important",
      color: "#4d4d4e",
      display: "block" as "block",
      fontSize: "1.6rem",
      minHeight: "100%",
      overflow: "auto !important" as "auto",
      padding: "6px 23px !important",
      width: "100%",
      wordWrap: "break-word" as "break-word"
    },
    "& .ck-toolbar": {
      "& .ck-button": {
        "&.ck-on": {
          background: "transparent !important",
          color: "#4d4d4e !important"
        },
        color: "#919191 !important",
        fontSize: "1.3rem",
        fontWeight: "normal" as "normal"
      },
      background: "transparent !important",
      border: "none !important",
      padding: "0 14px !important"
    },
    alignItems: "stretch",
    display: "flex" as "flex",
    flexDirection: "column-reverse" as "column-reverse",
    height: "152px",
    width: "100%"
  },
  disabled: {
    "& $editorWrapper": {
      maxHeight: 152
    },
    "& $bottomWrapper": {
      display: "none" as "none"
    },
    "& .ck-content": {
      background: "transparent !important"
    },
    backgroundColor: "#f5f5f5"
  },
  editorWrapper: {
    display: "flex" as "flex",
    flexGrow: 1,
    maxHeight: 110,
    overflow: "auto",
    overflowScrolling: "touch" as "touch",
    position: "relative" as "relative",
    width: "100%"
  },
  grow: {
    "& $editorWrapper": {
      [theme.breakpoints.up("md")]: {
        maxHeight: 152,
        overflow: "auto" as "auto"
      },
      maxHeight: "none !important" as "none",
      overflow: "visible" as "visible"
    },
    [theme.breakpoints.up("md")]: {
      height: 152
    },
    height: "auto" as "auto",
    minHeight: 152
  },
  invalid: {
    "& .ck-content:not(:focus-within)": {
      background: "#ffebee !important"
    }
  },
  readOnly: {
    "& $editorWrapper": {
      maxHeight: 152
    },
    "& .ck-content": {
      color: "graytext"
    },
    "& .ck-placeholder:before": {
      color: "graytext"
    }
  },
  resize: {
    "& $editorWrapper": {
      borderBottom: "1px solid #eee !important",
      maxHeight: "none" as "none",
      overflow: "auto",
      resize: "vertical" as "vertical"
    },
    "& .ck-content": {
      maxHeight: "none" as "none",
      minHeight: 152
    },
    height: "auto" as "auto",
    maxHeight: "none" as "none"
  },
  bottomWrapper: {
    height: "42px",
    display: "flex",
    flexFlow: "row nowrap",
    justifyContent: "space-between"
  },
  charCountWrapper: {
    display: "flex",
    alignItems: "center",
    fontSize: "1.3rem",
    padding: "0 14px"
  },
  charCountExceeded: {
    color: "red"
  }
}));

interface IOwnProps {
  defaultValue?: string;
  disabled?: boolean;
  enableHeadings?: boolean;
  onChange?: (data: string) => void;
  placeholder?: string;
  readOnly?: boolean;
  invalid?: boolean;
  dataTestId?: string;
  enableResize?: boolean;
  enableGrow?: boolean;
  maxChars?: number;
  onCharCountChange?: (count: number) => void;
}

const ckeditorLanguageMap = {
  en: "en",
  de: "de",
  ua: "uk",
  cz: "cs",
  es: "es",
  fr: "fr",
  it: "it",
  nl: "nl",
  pl: "pl",
  pt: "pt",
  ro: "ro"
};
const fallbackCkeditorLanguage = "en";

const TextEditor = ({
  defaultValue = "",
  disabled = false,
  onChange,
  placeholder,
  readOnly = false,
  invalid = false,
  enableHeadings = false,
  enableResize = false,
  enableGrow = false,
  dataTestId,
  maxChars,
  onCharCountChange
}: IOwnProps) => {
  const toolbarContainerRef = React.createRef<any>();
  const charCountContainerRef = React.useRef<HTMLDivElement>(null);
  const classes = useStyles();
  const {
    i18n: { language },
    t
  } = useTranslation();
  const previousCount = React.useRef<number | null>(null);
  const updateCharCount = (charsCount: number) => {
    if (maxChars === undefined || !charCountContainerRef.current) {
      return;
    }

    const charsLeft = maxChars - charsCount;

    if (maxChars !== undefined) {
      charCountContainerRef.current.textContent = `${charsLeft}`;
    }
    if (charsLeft < 0) {
      charCountContainerRef.current.classList.add(classes.charCountExceeded);
    } else {
      charCountContainerRef.current.classList.remove(classes.charCountExceeded);
    }
  };
  const wordCountConfig = React.useMemo(
    () => ({
      wordCount: {
        onUpdate: (stats: { characters: number }) => {
          if (stats.characters !== previousCount.current) {
            previousCount.current = stats.characters;
            onCharCountChange?.(stats.characters);
          }

          updateCharCount(stats.characters);
        }
      }
    }),
    [onCharCountChange]
  );
  const [loaded, updateLoaded] = React.useState(false);

  React.useEffect(() => {
    updateLoaded(false);
  }, [language, placeholder]);

  React.useEffect(() => {
    if (!loaded) {
      updateLoaded(true);
    }
  }, [loaded]);

  const handleChange = React.useCallback(
    (e: React.FormEvent, editor: any) => {
      if (onChange) {
        const html = editor.getData();
        onChange(isEmptyText(html) ? "" : html);
      }
    },
    [onChange]
  );

  const handleInit = React.useCallback(
    (editor: any) => {
      if (toolbarContainerRef.current) {
        toolbarContainerRef.current.appendChild(editor.ui.view.toolbar.element);
      }
      const wordCountPlugin = editor.plugins.get("WordCount");
      updateCharCount(wordCountPlugin.characters);
    },
    [toolbarContainerRef]
  );

  const ckeditorLanguage = ckeditorLanguageMap[language] ?? fallbackCkeditorLanguage;
  const [isTranslationLoaded, setIsTranslationLoaded] = React.useState(false);
  React.useEffect(() => {
    if (ckeditorLanguage !== "en") {
      require(`ckeditor5-custom-build/build/translations/${ckeditorLanguage}`);
    }
    setIsTranslationLoaded(true);
  }, [ckeditorLanguage]);

  const EditorConfig = React.useMemo(() => {
    return enableHeadings
      ? {
          heading: {
            options: [
              {
                class: "ck-heading_heading1",
                model: "heading1",
                title: "Heading 1",
                view: "h1"
              },
              {
                class: "ck-heading_heading3",
                model: "heading3",
                title: "Heading 3",
                view: "h3"
              },
              {
                class: "ck-heading_heading2",
                model: "heading2",
                title: "Heading 2",
                view: "h2"
              },
              {
                class: "ck-heading_heading4",
                model: "heading4",
                title: "Heading 4",
                view: "h4"
              },
              {
                class: "ck-heading_heading5",
                model: "heading5",
                title: "Heading 5",
                view: "h5"
              },
              {
                class: "ck-heading_heading6",
                model: "heading6",
                title: "Heading 6",
                view: "h6"
              },
              {
                class: "ck-heading_paragraph",
                model: "paragraph",
                title: "Paragraph"
              }
            ]
          },
          language: ckeditorLanguage,
          placeholder,
          toolbar: [...editorConfig.toolbar, "heading"],
          ...wordCountConfig
        }
      : {
          ...editorConfig,
          language: ckeditorLanguage,
          placeholder,
          ...wordCountConfig
        };
  }, [enableHeadings, placeholder, language]);

  const wrapperClassName = React.useMemo(() => {
    const classNames = [classes.CKEditorWrapper];
    if (disabled) {
      classNames.push(classes.disabled);
    }
    if (invalid) {
      classNames.push(classes.invalid);
    }
    if (readOnly) {
      classNames.push(classes.readOnly);
    }

    if (enableResize) {
      classNames.push(classes.resize);
    }

    if (enableGrow) {
      classNames.push(classes.grow);
    }

    return classNames.join(" ");
  }, [disabled, invalid, readOnly, enableResize, enableGrow]);

  return (
    <div className={wrapperClassName}>
      {loaded && isTranslationLoaded && (
        <>
          <div className={classes.bottomWrapper}>
            <div ref={toolbarContainerRef} />
            {maxChars !== undefined && (
              <div className={classes.charCountWrapper}>
                {t("feedback.common.charactersLeft")}:&nbsp;
                <span ref={charCountContainerRef} />
              </div>
            )}
          </div>
          <div className={classes.editorWrapper} data-qa-selector={dataTestId}>
            <CKEditor
              data={defaultValue}
              config={EditorConfig}
              disabled={disabled || readOnly}
              onChange={handleChange}
              onBlur={handleChange}
              onReady={handleInit}
              editor={Editor}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default React.memo(TextEditor);
